import type { CombineLevel } from '../../schemas/drill';
import { type Block, levelBlocks } from './catalog';
import type { AttemptLike } from './tally';

/**
 * Who shoots when. A level is cut into blocks (one spot, up to five shots) and a turn is one
 * shooter taking one block. Two rotations (gameplay.md "Rotation"):
 *
 *   spot    everyone shoots a spot, then the group walks to the next one (default)
 *   player  one shooter does the whole level, then the next
 *
 * The turn list is made once when the level starts and saved with the session. What has been shot
 * is never stored on the turns: `progressOf` reads it from the attempts, so an undo (a void) walks
 * the cursor back by itself.
 */

export type RotationMode = 'spot' | 'player';
export const ROTATION_MODES: readonly RotationMode[] = ['spot', 'player'];

export interface Turn {
  blockIndex: number;
  playerId: string;
}

export function buildTurns(level: CombineLevel, playerIds: readonly string[], mode: RotationMode): Turn[] {
  const blocks = levelBlocks(level);
  const out: Turn[] = [];
  if (mode === 'spot') {
    for (const b of blocks) for (const p of playerIds) out.push({ blockIndex: b.index, playerId: p });
  } else {
    for (const p of playerIds) for (const b of blocks) out.push({ blockIndex: b.index, playerId: p });
  }
  return out;
}

/** Seeded or plain Fisher-Yates. `rng` returns [0, 1). Does not mutate. */
export function shuffled<T>(items: readonly T[], rng: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

/**
 * "Order is randomized at level 1, then each next level starts with the person who went last."
 * Rotates `order` so `lastShooter` comes first. An id that is not in the list leaves it alone.
 */
export function startWith(order: readonly string[], lastShooter: string | null | undefined): string[] {
  const i = lastShooter ? order.indexOf(lastShooter) : -1;
  if (i <= 0) return [...order];
  return [...order.slice(i), ...order.slice(0, i)];
}

export interface TurnProgress {
  turn: Turn;
  block: Block;
  recorded: number;
  complete: boolean;
}

export interface Progress {
  turns: TurnProgress[];
  /** Index of the first turn still to shoot, or turns.length when the level is done. */
  cursor: number;
  current: TurnProgress | null;
  complete: boolean;
  shotsRecorded: number;
  shotsTotal: number;
}

/**
 * Hand the recorded shots to the turns. A player's shots at one spot fill that spot's blocks in
 * order (Charity Stripe has two blocks at the line), whatever order the turns come in.
 */
export function progressOf(level: CombineLevel, turns: readonly Turn[], attempts: readonly AttemptLike[]): Progress {
  const blocks = levelBlocks(level);
  const byIndex = new Map(blocks.map((b) => [b.index, b]));
  const shots = new Map<string, number>();
  for (const a of attempts) {
    const k = `${a.playerId}|${a.spot ?? ''}`;
    shots.set(k, (shots.get(k) ?? 0) + 1);
  }
  // Remaining shots of each (player, spot) pool, handed out block by block in index order.
  const pool = new Map(shots);
  const recordedFor = new Map<string, number>();
  const players = [...new Set(turns.map((t) => t.playerId))];
  for (const p of players) {
    for (const b of blocks) {
      const k = `${p}|${b.spot}`;
      const have = pool.get(k) ?? 0;
      const take = Math.min(b.count, have);
      pool.set(k, have - take);
      recordedFor.set(`${p}|${b.index}`, take);
    }
  }
  const out: TurnProgress[] = turns.map((turn) => {
    const block = byIndex.get(turn.blockIndex) as Block;
    const recorded = recordedFor.get(`${turn.playerId}|${turn.blockIndex}`) ?? 0;
    return { turn, block, recorded, complete: recorded >= block.count };
  });
  const cursor = out.findIndex((t) => !t.complete);
  return {
    turns: out,
    cursor: cursor < 0 ? out.length : cursor,
    current: cursor < 0 ? null : (out[cursor] as TurnProgress),
    complete: cursor < 0,
    shotsRecorded: out.reduce((n, t) => n + Math.min(t.recorded, t.block.count), 0),
    shotsTotal: out.reduce((n, t) => n + t.block.count, 0),
  };
}

/**
 * "Skip for now". In spot rotation the current shooter drops to the end of this spot. In player
 * rotation the whole rest of their level goes behind the next shooter. A lone shooter, or the
 * last one in line, changes nothing.
 */
export function skipTurn(turns: readonly Turn[], cursor: number, mode: RotationMode): Turn[] {
  const t = turns[cursor];
  if (!t) return [...turns];
  if (mode === 'spot') {
    let end = cursor;
    while (turns[end + 1]?.blockIndex === t.blockIndex) end += 1;
    if (end === cursor) return [...turns];
    return [...turns.slice(0, cursor), ...turns.slice(cursor + 1, end + 1), t, ...turns.slice(end + 1)];
  }
  let runEnd = cursor;
  while (turns[runEnd + 1]?.playerId === t.playerId) runEnd += 1;
  const nextPlayer = turns[runEnd + 1]?.playerId;
  if (nextPlayer === undefined) return [...turns];
  let nextEnd = runEnd + 1;
  while (turns[nextEnd + 1]?.playerId === nextPlayer) nextEnd += 1;
  return [
    ...turns.slice(0, cursor),
    ...turns.slice(runEnd + 1, nextEnd + 1),
    ...turns.slice(cursor, runEnd + 1),
    ...turns.slice(nextEnd + 1),
  ];
}

/** Who shoots after the current turn: the next distinct shooters, up to `n`. */
export function upNext(progress: Progress, n = 2): string[] {
  const cur = progress.current?.turn.playerId;
  const out: string[] = [];
  for (let i = progress.cursor + 1; i < progress.turns.length && out.length < n; i++) {
    const p = (progress.turns[i] as TurnProgress).turn.playerId;
    if (p !== cur && !out.includes(p) && !(progress.turns[i] as TurnProgress).complete) out.push(p);
  }
  return out;
}
